import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  skipToken,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AccountType,
  CompanyType,
  Driver_Read,
  getV1CompaniesAccountsTypeahead,
  GetV1CompaniesAccountsTypeaheadData,
  getV1CompaniesCompanyIdAccountsFacetedSearchCustomer,
  GetV1CompaniesCompanyIdAccountsFacetedSearchCustomerData,
  getV1CompaniesCompanyIdAccountsFacetedSearchVendor,
  GetV1CompaniesCompanyIdAccountsFacetedSearchVendorData,
  getV1CompaniesCompanyIdDepartmentsFacetedSearch,
  GetV1CompaniesCompanyIdDepartmentsFacetedSearchData,
  getV1CompaniesCompanyIdDriversFacetedSearch,
  GetV1CompaniesCompanyIdDriversFacetedSearchData,
  getV1CompaniesCompanyIdEquipmentFacetedSearch,
  GetV1CompaniesCompanyIdEquipmentFacetedSearchData,
  getV1CompaniesCompanyIdEquipmentTypesFacetedSearch,
  GetV1CompaniesCompanyIdEquipmentTypesFacetedSearchData,
  getV1CompaniesCompanyIdForemenFacetedSearch,
  GetV1CompaniesCompanyIdForemenFacetedSearchData,
  getV1CompaniesCompanyIdMaterialsFacetedSearch,
  GetV1CompaniesCompanyIdMaterialsFacetedSearchData,
  getV1CompaniesCompanyIdOrdersDispatchNumbersFacetedSearch,
  GetV1CompaniesCompanyIdOrdersDispatchNumbersFacetedSearchData,
  getV1CompaniesCompanyIdProjectsExternalIdsTypeahead,
  getV1CompaniesCompanyIdProjectsFacetedSearch,
  GetV1CompaniesCompanyIdProjectsFacetedSearchData,
  getV1CompaniesCompanyIdServiceClassesFacetedSearch,
  GetV1CompaniesCompanyIdServiceClassesFacetedSearchData,
  getV1CompaniesCompanyIdSitesFacetedSearchDropoff,
  GetV1CompaniesCompanyIdSitesFacetedSearchDropoffData,
  getV1CompaniesCompanyIdSitesFacetedSearchPickup,
  GetV1CompaniesCompanyIdSitesFacetedSearchPickupData,
  getV1CompaniesCompanyIdUsersCollaboratorsFacetedSearch,
  GetV1CompaniesCompanyIdUsersCollaboratorsFacetedSearchData,
  getV1CompaniesCompanyIdUsersRequestersFacetedSearch,
  GetV1CompaniesCompanyIdUsersRequestersFacetedSearchData,
  getV1CompaniesCompanyIdUsersSalespeopleFacetedSearch,
  GetV1CompaniesCompanyIdUsersSalespeopleFacetedSearchData,
  OrderState,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { noop, uniqBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

import {
  FilterMenuItemLoadingReason,
  FilterMenuOption,
} from '~components/Filters/FilterMenuItem';
import { SelectedFilter } from '~constants/enums';
import { AccountTypeahead } from '~hooks/useAccount/models';
import { BasicDepartment } from '~hooks/useDepartment/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import { EquipmentTypeahead, EquipmentTypeTypeahead } from '~hooks/useEquipment/models';
import { ForemanTypeahead } from '~hooks/useForeman/models';
import { toMaterialTypeaheadOption } from '~hooks/useMaterials/typeaheadOption';
import { ProjectExternalIdTypeahead, ProjectTypeahead } from '~hooks/useProjects/models';
import { BasicServiceClass } from '~hooks/useServiceClass/models';
import { SiteTypeahead } from '~hooks/useSites/models';
import { UserTypeahead } from '~hooks/useUsers/models';
import { buildEquipmentOwnerOptions } from '~queries/equipmentOwnerOptions';
import { extractPagination, Pagination } from '~services/pagination';
import { Filters } from '~store/OrdersDispatchStore';
import { getConsolidatedEquipmentName } from '~utils/drivers/driver-utils';
import { getEquipmentLabel } from '~utils/equipment';
import { ActingCompany } from '~utils/vendorCompanyOptions';

const QUERY_PAGINATION_LIMIT = 10;

// Filters backed by static/local option lists (useDummyQuery) rather than an API typeahead.
// Shared so the Filters dropdown and the omni-search bar stay in sync.
export const DUMMY_QUERY_FILTERS: SelectedFilter[] = [
  SelectedFilter.AI_REVIEW_STATUSES,
  SelectedFilter.DISPATCH_STATUSES,
  SelectedFilter.JOB_STATES,
  SelectedFilter.ORDER_STATES_REFINED,
  SelectedFilter.PREVAILING_WAGE,
  SelectedFilter.SITE_TYPES,
  SelectedFilter.DUPLICATE_TICKETS,
  SelectedFilter.TICKET_TYPES,
];

export interface OrderDispatchQuery {
  clear: () => void;
  data: FilterMenuOption[];
  fetch: (searchQuery: string) => void;
  fetchMore:
    | ((
        options?: FetchNextPageOptions | undefined,
      ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<FilterMenuOption[], unknown>, Error>
      >)
    | undefined;
  loadingReason?: FilterMenuItemLoadingReason;
}

// Every dispatch-facet endpoint accepts the full documented cross-filter set except its own
// self-referencing key (e.g. filter[job][driver_ids] on the drivers endpoint), which the backend
// rejects outright rather than merely ignoring. So no single endpoint's query type carries every
// field this helper needs to build. The drivers and service-classes endpoints are each missing
// only their own self-key (filter[job][driver_ids] and filter[service_class_ids] respectively)
// relative to the full set, so intersecting them recovers a complete type with no
// endpoint-specific required fields mixed in.
type FacetSearchQueryParams = NonNullable<
  GetV1CompaniesCompanyIdDriversFacetedSearchData['query']
> &
  NonNullable<GetV1CompaniesCompanyIdServiceClassesFacetedSearchData['query']>;

type QueryParamsState = {
  shouldFetch: boolean;
  filters: { query: string };
  pagination?: Pagination & { hasMore: boolean };
};

type SimpleQueryParams = Pick<
  NonNullable<GetV1CompaniesAccountsTypeaheadData['query']>,
  'page[after]' | 'page[limit]' | 'search[query]'
>;

function deriveSimpleQueryParams(queryParams: QueryParamsState) {
  const { filters, pagination } = queryParams;

  const query: SimpleQueryParams = { 'page[limit]': QUERY_PAGINATION_LIMIT };

  if (pagination?.after) {
    query['page[after]'] = pagination.after;
  }

  if (filters.query.trim().length) {
    query['search[query]'] = filters.query.trim();
  }

  return query;
}

/**
 * Customers and Vendors
 */
const CUSTOMER_ACCOUNTS_SELF_FACET: keyof FacetSearchQueryParams =
  'filter[job][customer_account_ids]';
const VENDOR_ACCOUNTS_SELF_FACET: keyof FacetSearchQueryParams =
  'filter[job][vendor_account_ids]';

export function useCustomersQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_customers';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdAccountsFacetedSearchCustomerData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            CUSTOMER_ACCOUNTS_SELF_FACET,
            pageParam,
          );

          return getV1CompaniesCompanyIdAccountsFacetedSearchCustomer({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const accounts = response.data.data.map((account) => {
              const parsed = AccountTypeahead.parse(account);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return accounts;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

export function useEquipmentOwnersQuery(
  companyId: string,
  actingCompany: ActingCompany,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_equipmentOwners';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId, actingCompany.id],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? async ({ pageParam, signal }) => {
          const query: GetV1CompaniesAccountsTypeaheadData['query'] =
            deriveSimpleQueryParams({
              ...queryParams,
              pagination: pageParam,
            });
          query['filter[account_types]'] = [AccountType.VENDOR];
          // Same source as the equipment dialog's Owned by picker
          query['filter[connected]'] = true;

          const response = await getV1CompaniesAccountsTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          });
          const options = buildEquipmentOwnerOptions(
            response.data.data,
            actingCompany,
            queryParams.filters.query,
            !pageParam?.after,
          );
          const pagination = extractPagination(response);

          setQueryParams((state) => ({
            ...state,
            pagination: { ...pagination, hasMore: Boolean(pagination.after) },
          }));

          return options;
        }
      : skipToken,
  });

  // Two vendor accounts can resolve to the same company across pages
  const allData = useMemo(
    () => uniqBy((data?.pages ?? []).flat(), (option) => option.value),
    [data?.pages],
  );

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

export function useVendorsQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_vendors';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdAccountsFacetedSearchVendorData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            VENDOR_ACCOUNTS_SELF_FACET,
            pageParam,
          );

          return getV1CompaniesCompanyIdAccountsFacetedSearchVendor({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const accounts = response.data.data.map((account) => {
              const parsed = AccountTypeahead.parse(account);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return accounts;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Dispatch Numbers
 */
export function useDispatchNumbersQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey =
    'OrdersDispatchFilters_getV1CompaniesCompanyIdOrdersDispatchNumbersFacetedSearch';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdOrdersDispatchNumbersFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[dispatch_number_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdOrdersDispatchNumbersFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const dispatchNumbers = response.data.data.map((dispatchNumber) => {
              return { label: dispatchNumber.name, value: dispatchNumber.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return dispatchNumbers;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Drivers
 */
export function useDriversQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_drivers';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdDriversFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[job][driver_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdDriversFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const drivers = response.data.data.map((driver) => {
              const parsed = DriverBasic.parse(driver as Driver_Read);

              const equipmentName = getConsolidatedEquipmentName(parsed);
              const label = equipmentName
                ? `${equipmentName} - ${parsed.fullName}`
                : parsed.fullName;

              return { label, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return drivers;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Equipment Types
 */
export function useEquipmentTypesQuery(
  companyId: string,
  filtersInStore: Filters = {},
): OrderDispatchQuery {
  const queryKey =
    'OrdersDispatchFilters_getV1CompaniesCompanyIdEquipmentTypesFacetedSearch';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdEquipmentTypesFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[equipment_type_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdEquipmentTypesFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const equipmentTypes = response.data.data.map((equipmentType) => {
              const parsed = EquipmentTypeTypeahead.parse(equipmentType);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return equipmentTypes;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Foremen
 */
export function useForemenQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFilters_getV1CompaniesCompanyIdForemenFacetedSearch';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdForemenFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[job][foreman_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdForemenFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const foremen = response.data.data.map((foreman) => {
              const parsed = ForemanTypeahead.parse(foreman);

              return { label: parsed.fullName, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return foremen;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Materials
 */
export function useMaterialsQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_materials';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdMaterialsFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[job][material_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdMaterialsFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const materials = response.data.data.map(toMaterialTypeaheadOption);
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return materials;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Applies the common set of faceted-search filters to the query object.
 *
 * @param query - The query object to apply the filters to.
 * @param filtersInStore - The filters to apply to the query.
 * @param currentFacet - The self-referencing key this endpoint omits from its own query type.
 * Removed from the merged cross-filters so it's never sent back to its own endpoint.
 * @param pagination - The pagination object to apply to the query.
 */
function applyDispatchFacetQueryParams<Q extends FacetSearchQueryParams>(
  query: Q,
  filtersInStore: Filters,
  currentFacet: keyof FacetSearchQueryParams,
  pagination: Pagination | undefined,
) {
  // Built separately from `query` because each endpoint's query type omits its own
  // self-referencing key(s) entirely — FacetSearchQueryParams is the only type that carries
  // all of them, so those keys can be deleted here before merging into `query`.
  const crossFilters: FacetSearchQueryParams = {};

  crossFilters['page[limit]'] = QUERY_PAGINATION_LIMIT;

  if (pagination?.after) {
    crossFilters['page[after]'] = pagination.after;
  }

  // TODO: The API supports filter[job][company_ids] but Filters does not. If ever added, that can go here.

  if (filtersInStore?.search?.trim().length) {
    crossFilters['search[job][datagrid]'] = filtersInStore.search.trim();
  }

  if (filtersInStore?.aiReviewStatuses?.length) {
    crossFilters['filter[job][ai_review][statuses]'] = filtersInStore.aiReviewStatuses;
  }

  if (filtersInStore?.customerAccountIds?.length) {
    crossFilters['filter[job][customer_account_ids]'] = filtersInStore.customerAccountIds;
  }

  if (filtersInStore?.departmentIds?.length) {
    crossFilters['filter[department_ids]'] = filtersInStore.departmentIds;
  }

  if (filtersInStore?.dispatchNumberIds?.length) {
    crossFilters['filter[dispatch_number_ids]'] = filtersInStore.dispatchNumberIds;
  }

  if (filtersInStore?.driverIds?.length) {
    crossFilters['filter[job][driver_ids]'] = filtersInStore.driverIds;
  }

  if (filtersInStore?.dropoffSites?.length) {
    crossFilters['filter[job][dropoff_site_ids]'] = filtersInStore.dropoffSites;
  }

  if (filtersInStore?.endDate?.length) {
    crossFilters['filter[job][end_date]'] = dayjs
      .tz(filtersInStore.endDate)
      .endOf('day')
      .toISOString();
  }

  if (filtersInStore?.equipmentIds?.length) {
    crossFilters['filter[job][equipment_ids]'] = filtersInStore.equipmentIds;
  }

  if (filtersInStore?.equipmentTypes?.length) {
    crossFilters['filter[equipment_type_ids]'] = filtersInStore.equipmentTypes;
  }

  if (filtersInStore?.projectsExternalIds?.length) {
    crossFilters['filter[job][external_ids]'] = filtersInStore.projectsExternalIds;
  }

  if (filtersInStore?.foremen?.length) {
    crossFilters['filter[job][foreman_ids]'] = filtersInStore.foremen;
  }

  if (filtersInStore?.materialIds?.length) {
    crossFilters['filter[job][material_ids]'] = filtersInStore.materialIds;
  }

  if (filtersInStore?.pickUpSites?.length) {
    crossFilters['filter[job][pickup_site_ids]'] = filtersInStore.pickUpSites;
  }

  if (filtersInStore?.prevailingWage?.length === 1) {
    crossFilters['filter[job][prevailing_wage]'] =
      filtersInStore.prevailingWage[0] === 'true';
  }

  if (filtersInStore?.projectIds?.length) {
    crossFilters['filter[job][project_ids]'] = filtersInStore.projectIds;
  }

  if (filtersInStore?.requesterIds?.length) {
    crossFilters['filter[requester_ids]'] = filtersInStore.requesterIds;
  }

  if (filtersInStore?.salespersonIds?.length) {
    crossFilters['filter[job][salesperson_ids]'] = filtersInStore.salespersonIds;
  }

  if (filtersInStore?.serviceClassIds?.length) {
    crossFilters['filter[service_class_ids]'] = filtersInStore.serviceClassIds;
  }

  if (filtersInStore?.siteTypes?.length) {
    crossFilters['filter[job][site_types]'] = filtersInStore.siteTypes;
  }

  if (filtersInStore?.startDate?.length) {
    crossFilters['filter[job][start_date]'] = dayjs
      .tz(filtersInStore.startDate)
      .startOf('day')
      .toISOString();
  }

  if (filtersInStore?.jobStates?.length) {
    crossFilters['filter[job][states]'] = filtersInStore.jobStates;
  }

  if (filtersInStore?.dispatchStatuses?.length) {
    crossFilters['filter[job][dispatch_statuses]'] = filtersInStore.dispatchStatuses;
  }

  if (filtersInStore?.ticketPotentialDuplicate?.length) {
    crossFilters['filter[job][ticket][potential_duplicate]'] =
      filtersInStore.ticketPotentialDuplicate[0] === 'true';
  }

  if (filtersInStore?.ticketTypes?.length === 1) {
    crossFilters['filter[default_digital_ticket_view]'] =
      filtersInStore.ticketTypes[0] === 'true';
  }

  if (filtersInStore?.hasUnreadMessages === true) {
    crossFilters['filter[has_unread_messages]'] = true;
  }

  if (filtersInStore?.vendorAccountIds?.length) {
    crossFilters['filter[job][vendor_account_ids]'] = filtersInStore.vendorAccountIds;
  }

  // Mirrors the External-tab precedence in useOrdersDispatch.ts's buildDispatchOrdersBaseQuery:
  // filter[external] and filter[states] are mutually exclusive, and refinedOrderStates (a
  // derived/narrowed set) takes precedence over the raw orderStates selection when both are set.
  if (filtersInStore?.external === true) {
    crossFilters['filter[external]'] = true;
  } else {
    if (filtersInStore?.external === false) {
      crossFilters['filter[external]'] = false;
    }

    if (filtersInStore?.orderStates?.length) {
      let states = filtersInStore.orderStates;

      if (filtersInStore?.refinedOrderStates?.length) {
        states = filtersInStore.refinedOrderStates;
      }

      // When searching, always include cancelled so users can find cancelled orders
      if (
        filtersInStore?.search?.trim().length &&
        !states.includes(OrderState.CANCELED)
      ) {
        states = [...states, OrderState.CANCELED];
      }

      crossFilters['filter[states]'] = states;
    }
  }

  delete crossFilters[currentFacet];

  Object.assign(query, crossFilters);
}

/**
 * Pick Up and Drop Off
 */
const PICKUP_SITES_SELF_FACET: keyof FacetSearchQueryParams =
  'filter[job][pickup_site_ids]';
const DROPOFF_SITES_SELF_FACET: keyof FacetSearchQueryParams =
  'filter[job][dropoff_site_ids]';

export function useDropOffSitesQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey =
    'OrdersDispatchFilters_getV1CompaniesCompanyIdSitesFacetedSearchDropoff';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdSitesFacetedSearchDropoffData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            DROPOFF_SITES_SELF_FACET,
            pageParam,
          );

          return getV1CompaniesCompanyIdSitesFacetedSearchDropoff({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const dropOffSites = response.data.data.map((account) => {
              const parsed = SiteTypeahead.parse(account);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return dropOffSites;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

export function usePickUpSitesQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey =
    'OrdersDispatchFilters_getV1CompaniesCompanyIdSitesFacetedSearchPickup';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdSitesFacetedSearchPickupData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            PICKUP_SITES_SELF_FACET,
            pageParam,
          );

          return getV1CompaniesCompanyIdSitesFacetedSearchPickup({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const dropOffSites = response.data.data.map((account) => {
              const parsed = SiteTypeahead.parse(account);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return dropOffSites;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Projects
 */
export function useProjectsQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_projects';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdProjectsFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[job][project_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdProjectsFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const projects = response.data.data.map((project) => {
              const parsed = ProjectTypeahead.parse(project);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return projects;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Project External IDs
 */
export function useProjectExternalIdsQuery(companyId: string): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_projectExternalIds';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveSimpleQueryParams({
            ...queryParams,
            pagination: pageParam,
          });

          return getV1CompaniesCompanyIdProjectsExternalIdsTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const projectExternalIds = response.data.data
              .map((projectExternalId) => {
                const parsed = ProjectExternalIdTypeahead.parse(projectExternalId);

                return { label: parsed.externalId, value: parsed.externalId };
              })
              .filter((item): item is { label: string; value: string } => {
                return Boolean(item.label && item.value);
              });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return projectExternalIds;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Requesters
 */
export function useRequestersQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_requesters';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdUsersRequestersFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          // filter[collaborator_ids] is an order-level facet exposed only by the
          // Users-family faceted-search endpoints (Requesters, Salespeople,
          // Collaborators) — the shared applyDispatchFacetQueryParams base type doesn't
          // include it, so it's forwarded here rather than there.
          if (filtersInStore?.collaboratorIds?.length) {
            query['filter[collaborator_ids]'] = filtersInStore.collaboratorIds;
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[requester_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdUsersRequestersFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const requesters = response.data.data.map((requester) => {
              const parsed = UserTypeahead.parse(requester);

              return { label: parsed.fullName, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return requesters;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Salespeople
 */
export function useSalespeopleQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_salespeople';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdUsersSalespeopleFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          // See the matching comment in useRequestersQuery above.
          if (filtersInStore?.collaboratorIds?.length) {
            query['filter[collaborator_ids]'] = filtersInStore.collaboratorIds;
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[job][salesperson_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdUsersSalespeopleFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const salespeople = response.data.data.map((salesperson) => {
              const parsed = UserTypeahead.parse(salesperson);

              return { label: parsed.fullName, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return salespeople;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Service Classes
 */
export function useServiceClassesQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey =
    'OrdersDispatchFilters_getV1CompaniesCompanyIdServiceClassesFacetedSearch';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdServiceClassesFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[service_class_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdServiceClassesFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const serviceClasses = response.data.data.map((serviceClass) => {
              const parsed = BasicServiceClass.parse(serviceClass);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return serviceClasses;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Dummy hook that satifies the "OrderDispatchQuery" contract. To be used with static data.
 */
export function useDummyQuery(data: FilterMenuOption[]): OrderDispatchQuery {
  const emptyFn = useCallback(() => {
    return noop;
  }, []);

  return { clear: emptyFn, data, fetch: emptyFn, fetchMore: undefined };
}

/**
 * Company Types
 */
export function useCompanyTypesQuery(): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_companyTypes';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? () => {
          return Promise.resolve().then(() => {
            const companyTypes = Object.values(CompanyType).filter((companyType) => {
              return companyType
                .toLowerCase()
                .includes(queryParams.filters.query.toLowerCase());
            });

            const data = companyTypes.map((companyType) => ({
              label: t(`enums.company_type.${companyType}`),
              value: companyType,
            }));

            return data;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Departments
 */
export function useDepartmentsQuery(
  companyId: string,
  filtersInStore: Filters = {},
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFiltersQueries_departments';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdDepartmentsFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[department_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdDepartmentsFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const departments = response.data.data.map((department) => {
              const parsed = BasicDepartment.parse(department);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return departments;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Equipment (units)
 */
export function useEquipmentQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey = 'OrdersDispatchFilters_getV1CompaniesCompanyIdEquipmentFacetedSearch';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdEquipmentFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[job][equipment_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdEquipmentFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const equipment = response.data.data.map((item) => {
              const parsed = EquipmentTypeahead.parse(item);

              return { label: getEquipmentLabel(parsed), value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return equipment;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Collaborators
 */
export function useCollaboratorsQuery(
  companyId: string,
  filtersInStore: Filters,
): OrderDispatchQuery {
  const queryKey =
    'OrdersDispatchFilters_getV1CompaniesCompanyIdUsersCollaboratorsFacetedSearch';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query: NonNullable<
            GetV1CompaniesCompanyIdUsersCollaboratorsFacetedSearchData['query']
          > = {};

          if (queryParams.filters.query.trim().length) {
            query['facet[search][query]'] = queryParams.filters.query.trim();
          }

          applyDispatchFacetQueryParams(
            query,
            filtersInStore,
            'filter[collaborator_ids]',
            pageParam,
          );

          return getV1CompaniesCompanyIdUsersCollaboratorsFacetedSearch({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const collaborators = response.data.data.map((collaborator) => {
              const parsed = UserTypeahead.parse(collaborator);

              return { label: parsed.fullName, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return collaborators;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}
